// Mock data for Deriv Affiliate Landing Page

export const heroData = {
  title: "Start Synthetic Trading with a Trusted Platform",
  subtitle: "Step-by-Step Guide to Trade Synthetic Indices on Deriv",
  description: "Learn synthetic trading with practical guidance, risk awareness, and beginner-friendly walkthroughs.",
  ctaText: "Start Trading Safely",
  ctaSecondary: "Learn More & Get Started",
  backgroundImage: "https://images.unsplash.com/photo-1569025743873-ea3a9ade89f9"
};

export const featuresData = [
  {
    id: 1,
    title: "Understand Synthetic Indices",
    description: "Learn how synthetic indices work, including market behavior, volatility patterns, and practical trade planning.",
    icon: "TrendingUp",
    image: "https://images.unsplash.com/photo-1639825988283-39e5408b75e8"
  },
  {
    id: 2,
    title: "Safe Start for Beginners",
    description: "Follow a structured beginner path with clear steps for setup, practice, and controlled live trading.",
    icon: "Zap",
    image: "https://images.unsplash.com/photo-1633769572945-936be251644b"
  },
  {
    id: 3,
    title: "Crash & Boom Trading Focus",
    description: "Explore crash boom trading concepts with educational examples and risk-aware execution tips.",
    icon: "Smartphone",
    image: "https://images.unsplash.com/photo-1611967564293-9cc64a2fd3dd"
  },
  {
    id: 4,
    title: "Risk Management Basics",
    description: "Build disciplined habits with position sizing, stop-loss planning, and emotional control guidelines.",
    icon: "BarChart3",
    image: "https://images.pexels.com/photos/6770521/pexels-photo-6770521.jpeg"
  },
  {
    id: 5,
    title: "Educational Trading Roadmap",
    description: "Use checklists and step-by-step lessons designed for synthetic trading and synthetic indices.",
    icon: "DollarSign",
    image: "https://images.unsplash.com/photo-1634024520950-cb6b612f9bde"
  },
  {
    id: 6,
    title: "Practice Before Live Trading",
    description: "Test your strategy on demo first, then move to live trading only when your process is consistent.",
    icon: "Award",
    image: "https://images.pexels.com/photos/28504954/pexels-photo-28504954.jpeg"
  }
];

export const benefitsData = [
  {
    id: 1,
    title: "Educational First Approach",
    description: "This site focuses on trading education to help beginners build safe and informed decision-making.",
    icon: "Shield"
  },
  {
    id: 2,
    title: "Synthetic Trading Focus",
    description: "All guidance is tailored to synthetic trading, synthetic indices, and crash boom trading.",
    icon: "Target"
  },
  {
    id: 3,
    title: "Step-by-Step Learning",
    description: "Progress from account setup to strategy planning with a clear learning sequence for beginners.",
    icon: "Layers"
  },
  {
    id: 4,
    title: "24/7 Support",
    description: "Round-the-clock customer support in multiple languages via chat, email, and phone, helping traders across Africa whenever markets move.",
    icon: "Headphones"
  },
  {
    id: 5,
    title: "Deriv Trading Guide",
    description: "Understand how to use Deriv as a third-party platform while keeping control of your risk decisions.",
    icon: "BookOpen"
  },
  {
    id: 6,
    title: "Secure Trading",
    description: "Your funds are kept in segregated accounts, protected by advanced encryption.",
    icon: "Lock"
  }
];

export const tradingInstruments = [
  {
    id: 1,
    name: "Synthetic Indices",
    description: "Price behavior driven by simulated market conditions with continuous availability",
    icon: "DollarSign",
    pairs: "Volatility indices and synthetic index variants"
  },
  {
    id: 2,
    name: "Crash & Boom",
    description: "Specialized synthetic instruments designed around spike behavior patterns",
    icon: "Bitcoin",
    pairs: "Crash and Boom index families"
  },
  {
    id: 3,
    name: "Range Break",
    description: "Synthetic indices that move within ranges before directional breaks",
    icon: "Gem",
    pairs: "Range-based synthetic index categories"
  },
  {
    id: 4,
    name: "Jump Indices",
    description: "Synthetic indices with occasional jump behavior for specialized strategies",
    icon: "LineChart",
    pairs: "Jump-focused synthetic index categories"
  }
];

export const testimonialsData = [
  {
    id: 1,
    name: "Michael Chen",
    role: "Synthetic Indices Trader",
    country: "Singapore",
    rating: 5,
    text: "The step-by-step synthetic trading guidance made it easier to build a consistent routine and manage risk better.",
    avatar: "MC"
  },
  {
    id: 2,
    name: "Sarah Johnson",
    role: "Part-Time Trader",
    country: "United Kingdom",
    rating: 5,
    text: "Perfect for beginners. The learning path explained synthetic indices clearly and helped me avoid rushed decisions.",
    avatar: "SJ"
  },
  {
    id: 3,
    name: "Ahmed Al-Rashid",
    role: "Crash Boom Trader",
    country: "UAE",
    rating: 5,
    text: "I came for crash boom trading guidance and found practical rules for entries, exits, and risk control.",
    avatar: "AA"
  },
  {
    id: 4,
    name: "Maria Garcia",
    role: "Synthetic Trading Learner",
    country: "Spain",
    rating: 5,
    text: "The educational format is clear and helpful. It explains synthetic trading in a way that beginners can follow.",
    avatar: "MG"
  }
];

export const faqData = [
  {
    id: 1,
    question: "How do I create a trading account?",
    answer: "Simply click the 'Start Trading Now' button, select your country, provide your email, and complete the registration. You'll get instant access to a demo account with $10,000 virtual funds to practice."
  },
  {
    id: 2,
    question: "What is the minimum deposit amount?",
    answer: "The minimum deposit varies by payment method and region, typically starting from $5-$10 USD. You can start small and scale up as you gain confidence."
  },
  {
    id: 3,
    question: "How long do withdrawals take?",
    answer: "Withdrawal processing times depend on your chosen payment method. E-wallet withdrawals are typically processed within 24 hours, while bank transfers may take 2-5 business days."
  },
  {
    id: 4,
    question: "Is there a demo account available?",
    answer: "Yes! All new accounts get free access to a demo account with $10,000 virtual funds. Practice trading in real market conditions without any risk."
  },
  {
    id: 5,
    question: "What trading platforms are available?",
    answer: "We offer multiple platforms: Metatrader5 (Metatrader5) for advanced traders, DTrader for quick trading, DBot for automated strategies, and SmartTrader for options trading."
  },
  {
    id: 6,
    question: "Is Deriv regulated and safe?",
    answer: "Yes, Deriv is regulated by multiple authorities including the Malta Financial Services Authority (MFSA). Client funds are kept in segregated accounts for maximum security."
  },
  {
    id: 7,
    question: "Which markets does this guide focus on?",
    answer: "This guide focuses on synthetic trading, synthetic indices, and crash boom trading education."
  },
  {
    id: 8,
    question: "Do you offer customer support?",
    answer: "Yes, we provide 24/7 customer support in multiple languages via live chat, email, and phone. Our support team is always ready to help with any questions."
  },
  {
    id: 9,
    question: "Can I use Deriv from Nigeria, South Africa, Botswana, or Zimbabwe?",
    answer: "Traders from many African countries, including Nigeria, South Africa, Botswana, and Zimbabwe, can apply to open an account with Deriv, subject to local regulations and Deriv's own eligibility checks. During sign-up, simply select your country and follow the on-screen instructions on the official Deriv website."
  }
];

export const statsData = [
  {
    id: 1,
    value: "3M+",
    label: "Global Traders",
    icon: "Users"
  },
  {
    id: 2,
    value: "247+",
    label: "Trading Instruments",
    icon: "TrendingUp"
  },
  {
    id: 3,
    value: "24/7",
    label: "Trading Available",
    icon: "Clock"
  },
  {
    id: 4,
    value: "25+",
    label: "Years Experience",
    icon: "Award"
  }
];

export const platformsData = [
  {
    id: 1,
    name: "DTrader",
    description: "Simple and intuitive platform for quick trading with customizable charts.",
    icon: "Smartphone"
  },
  {
    id: 2,
    name: "Metatrader5",
    description: "Industry-standard platform with advanced tools for professional traders.",
    icon: "BarChart3"
  },
  {
    id: 3,
    name: "DBot",
    description: "Build automated trading bots without coding. Perfect for strategy testing.",
    icon: "Bot"
  },
  {
    id: 4,
    name: "SmartTrader",
    description: "Advanced options trading platform with comprehensive market analysis.",
    icon: "Brain"
  }
];
